import React, { useState, lazy } from 'react';
import { UploadIcon, SearchIcon, GridIcon, ListIcon, Trash2Icon, CopyIcon } from 'lucide-react';
import { useMediaLibrary } from '../../lib/cmsStore';
import { Button } from '../../components/ui/Button';
export function MediaLibrary() {
  const {
    items: media,
    saveItem,
    deleteItem,
    isLoading
  } = useMediaLibrary();
  const [view, setView] = useState<'grid' | 'list'>('grid');
  const [search, setSearch] = useState('');
  if (isLoading) return <div className="p-8 font-mono text-sm" data-id="element-1312">Loading...</div>;
  const filteredMedia = media.filter(m => m.name.toLowerCase().includes(search.toLowerCase()));
  const handleSimulateUpload = () => {
    const url = prompt('Enter image URL to simulate upload:');
    if (url) {
      const name = url.split('/').pop() || 'uploaded-file.jpg';
      saveItem({
        id: Math.random().toString(36).substr(2, 9),
        url,
        name,
        size: '1.5 MB',
        type: 'image/jpeg',
        alt: '',
        caption: '',
        uploadDate: new Date().toISOString().split('T')[0],
        tags: []
      });
    }
  };
  const handleCopy = (url: string) => {
    navigator.clipboard.writeText(url);
    alert('URL copied to clipboard!');
  };
  return <div className="p-8 max-w-7xl mx-auto" data-id="element-1313">
      <div className="flex items-center justify-between mb-8" data-id="element-1314">
        <div data-id="element-1315">
          <h1 className="font-display text-3xl font-bold tracking-tight" data-id="element-1316">
            Media Library
          </h1>
          <p className="font-mono text-sm text-muted-foreground mt-2" data-id="element-1317">
            Manage images, videos, and assets for your projects.
          </p>
        </div>
        <Button onClick={handleSimulateUpload} className="gap-2" data-id="element-1318">
          <UploadIcon className="w-4 h-4" data-id="element-1319" />
          Upload Files
        </Button>
      </div>

      <div className="flex flex-col md:flex-row gap-4 mb-8 items-center justify-between" data-id="element-1320">
        <div className="relative w-full md:w-96" data-id="element-1321">
          <SearchIcon className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" data-id="element-1322" />
          <input type="text" placeholder="Search media..." value={search} onChange={e => setSearch(e.target.value)} className="w-full h-10 pl-10 pr-4 bg-card border border-border font-mono text-sm focus:outline-none focus:border-primary" data-id="element-1323" />
        </div>

        <div className="flex items-center gap-2 bg-card border border-border p-1" data-id="element-1324">
          <button onClick={() => setView('grid')} className={`p-2 transition-colors ${view === 'grid' ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-1325">
            <GridIcon className="w-4 h-4" data-id="element-1326" />
          </button>
          <button onClick={() => setView('list')} className={`p-2 transition-colors ${view === 'list' ? 'bg-secondary text-foreground' : 'text-muted-foreground hover:text-foreground'}`} data-id="element-1327">
            <ListIcon className="w-4 h-4" data-id="element-1328" />
          </button>
        </div>
      </div>

      {view === 'grid' ? <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-id="element-1329">
          {filteredMedia.map(item => <div key={item.id} className="group border border-border bg-card overflow-hidden" data-id="element-1330">
              <div className="aspect-square relative overflow-hidden bg-secondary" data-id="element-1331">
                <img src={item.url} alt={item.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" loading="lazy" data-id="element-1332" />
                <div className="absolute inset-0 bg-background/80 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2" data-id="element-1333">
                  <Button variant="secondary" size="sm" className="px-2" title="Copy URL" onClick={() => handleCopy(item.url)} data-id="element-1334">
                    <CopyIcon className="w-4 h-4" data-id="element-1335" />
                  </Button>
                  <Button variant="danger" size="sm" className="px-2" title="Delete" onClick={() => {
              if (window.confirm('Delete this file?')) deleteItem(item.id);
            }} data-id="element-1336">
                    <Trash2Icon className="w-4 h-4" data-id="element-1337" />
                  </Button>
                </div>
              </div>
              <div className="p-3 border-t border-border" data-id="element-1338">
                <p className="font-mono text-xs font-bold truncate" title={item.name} data-id="element-1339">
                  {item.name}
                </p>
                <div className="flex items-center justify-between mt-1" data-id="element-1340">
                  <span className="font-mono text-[10px] text-muted-foreground" data-id="element-1341">
                    {item.size}
                  </span>
                  <span className="font-mono text-[10px] text-muted-foreground" data-id="element-1342">
                    {item.uploadDate}
                  </span>
                </div>
              </div>
            </div>)}
        </div> : <div className="bg-card border border-border divide-y divide-border" data-id="element-1343">
          {filteredMedia.map(item => <div key={item.id} className="flex items-center gap-4 p-4 hover:bg-secondary/50 transition-colors" data-id="element-1344">
              <div className="w-16 h-16 shrink-0 bg-secondary border border-border" data-id="element-1345">
                <img src={item.url} alt={item.name} className="w-full h-full object-cover" loading="lazy" data-id="element-1346" />
              </div>
              <div className="flex-1 min-w-0" data-id="element-1347">
                <p className="font-mono text-sm font-bold truncate" data-id="element-1348">
                  {item.name}
                </p>
                <p className="font-mono text-xs text-muted-foreground mt-1 truncate" data-id="element-1349">
                  {item.url}
                </p>
              </div>
              <div className="w-24 text-right font-mono text-xs text-muted-foreground" data-id="element-1350">
                {item.size}
              </div>
              <div className="w-32 text-right font-mono text-xs text-muted-foreground" data-id="element-1351">
                {item.uploadDate}
              </div>
              <div className="flex items-center gap-2 ml-4" data-id="element-1352">
                <Button variant="ghost" size="sm" className="px-2" onClick={() => handleCopy(item.url)} data-id="element-1353">
                  <CopyIcon className="w-4 h-4" data-id="element-1354" />
                </Button>
                <Button variant="ghost" size="sm" className="px-2 text-destructive hover:text-destructive" onClick={() => {
            if (window.confirm('Delete this file?')) deleteItem(item.id);
          }} data-id="element-1355">
                  <Trash2Icon className="w-4 h-4" data-id="element-1356" />
                </Button>
              </div>
            </div>)}
        </div>}
    </div>;
}